// THE STRIPE PAYMENT AREA
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { useCart } from "../components/Cart/CartContext.jsx";
import { useState, useEffect } from "react";
import QRCode from "qrcode";
import { addOrder } from "../firebase/firebaseUtils";

const ProcessingModal = ({ show }) => {
  if (!show) return null;
  return (
    <div className="modal-wrapper">
      <div className="modal-shell">
        <h3>Wild Card Generating...</h3>
        <p>Please do not close or refresh the page.</p>
        <div className="spinner" style={{ marginTop: "1rem" }} />
      </div>
    </div>
  );
};

export default function CheckoutForm({ clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const cardElement = elements?.getElement(CardNumberElement);
      if (!cardElement) {
        setError("Failed to load secure payment form. Please refresh or try another browser.");
      }
    }, 500); // wait .5 seconds for Stripe Elements to load

    return () => clearTimeout(timeout);
    }, [elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const paymentMethodData = {
      card: elements.getElement(CardNumberElement),
      billing_details: {
        email,
        address: { postal_code: zip }
      }
    };

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodData,
    });

    if (!stripe || !elements) {
      return (
        <div className="checkout-spinner">
          <p>Loading secure payment form...</p>
          <div className="spinner" />
        </div>
      );
    }

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    if (paymentIntent.status === "succeeded") {
      const stripePaymentIntentID = paymentIntent.id;
      const orderIDs = [];

      for (const item of cart) {
        const quantity = item.quantity || 1;

        for (let i = 0; i < quantity; i++) {
          const orderID = `${Date.now()}${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
          orderIDs.push(orderID);

          const customization = item.customization?.[i] || {};
          const recipientName = customization.recipientName || "";
          const recipientEmail = customization.recipientEmail || "";
          const recipientVideo = customization.video || "";
          const sendCardOn = customization.sendCardOn || null;

          const qrGeneratedDate = new Date();
          const qrExpireDate = new Date(qrGeneratedDate);
          qrExpireDate.setDate(qrGeneratedDate.getDate() + 30);

          await addOrder({
            orderID,
            stripePaymentIntentID,
            orderDate: new Date(),
            qrGeneratedDate,
            qrExpireDate,
            sendCardOn,
            customerName: "Stripe Customer",
            customerEmail: email,
            recipientName,
            recipientEmail,
            recipientVideo,
            codeName: item.id,
            itemName: item.UITitle,
            itemCategory: item.category,
            itemDescription: item.description,
            qrCodeFilename: `${orderID}.png`,
            pdfFilename: `${orderID}.pdf`
          });

          const qrUrl = `https://wildcard.introducingjeffrey.com/webAR/index.html?order=${orderID}&token=augmented-secret-KEY-042785`;

          try {
            const dataUrl = await QRCode.toDataURL(qrUrl, {
              errorCorrectionLevel: 'H',
              margin: 1,
              color: {
                dark: "#000000",
                light: "#ffffff"
              }
            });

            await fetch("https://wildcard.introducingjeffrey.com/php/uploadQR.php", {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: new URLSearchParams({ filename: `${orderID}.png`, data: dataUrl })
            });

            await fetch("https://wildcard.introducingjeffrey.com/php/createPDF.php", {
              method: "POST",
              headers: { "Content-Type": "application/x-www-form-urlencoded" },
              body: new URLSearchParams({
                template: item.id,
                orderID: orderID
              })
            });

            //"JPG" CREATED IN createPDF.php TOO!

          } catch (qrError) {
            console.error("QR Code generation failed:", qrError);
          }
        }
      }

      localStorage.setItem("checkoutCart", JSON.stringify(cart));
      localStorage.setItem("customerEmail", email);
      localStorage.setItem("orderIDs", JSON.stringify(orderIDs));
      clearCart();
      navigate(`/thankyou?paymentIntent=${stripePaymentIntentID}`);
    }
  };

  const elementStyle = {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#999" }
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "0.5rem",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    backgroundColor: "#fff",
    boxSizing: "border-box"
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <h3>💳 Payment Information</h3>

      {/* <label>Email Address</label>
      <input
        type="email"
        value={email}
        required
        onChange={(e) => setEmail(e.target.value)}
        className="checkout-input"
      /> */}

      <label>Card Number</label>
      <div className="card-field-wrapper">
        <p><em>4242 4242 4242 4242</em></p>
        <CardNumberElement options={{ style: elementStyle }} />
      </div>

      <div className="card-info-row">
        <div className="flex-col">
          <label>Expiry</label>
          <div className="card-field-wrapper">
            <CardExpiryElement options={{ style: elementStyle }} />
          </div>
        </div>

        <div className="flex-col">
          <label>CVC</label>
          <div className="card-field-wrapper">
            <CardCvcElement options={{ style: elementStyle }} />
          </div>
        </div>

        <div className="flex-col">
          <label>ZIP</label>
          <input
            type="text"
            value={zip}
            required
            onChange={(e) =>
              /^\d{0,5}$/.test(e.target.value) && setZip(e.target.value)
            }
            maxLength={5}
            pattern="\d{5}"
            inputMode="numeric"
            placeholder="ZIP"
            className="checkout-input"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="checkout-button"
      >
        {loading ? "Processing..." : "Pay Now"}
      </button>

      {error && <p className="checkout-error">{error}</p>}

      <div className="checkout-note">
        <p>
          🔒 Payments securely processed by{" "}
          <strong>
            <a
              href="https://stripe.com/security"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe
            </a>
          </strong>
          .
        </p>
      </div>
    <ProcessingModal show={loading} />
    </form>
  );
}
